import { endpoints } from '~/api/endpoints';
import { exportsSseBaseUrl } from '~/constants/config/env';
import { useAuthStore } from '~/store/auth.store';
import { useLocaleStore } from '~/store/locale.store';

import type { ExportStatusResponse } from '../types/export.types';

/**
 * Stream-level error carrying the upstream HTTP status and any Retry-After
 * hint. Consumers branch by `.status` (duck-typed) so the check survives
 * module-graph duplication (HMR, dual ESM/CJS). Mirrors
 * `NotificationsStreamError` in the notifications feature.
 */
export class ExportStreamError extends Error {
  readonly status: number;
  readonly retryAfterSeconds?: number;
  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ExportStreamError';
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Parse a Retry-After header (delta-seconds or HTTP-date) into seconds. Undefined when absent/malformed. */
function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;
  const trimmed = headerValue.trim();
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number.parseInt(trimmed, 10);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
  }
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  const deltaMs = dateMs - Date.now();
  return deltaMs > 0 ? Math.ceil(deltaMs / 1000) : 0;
}

export interface ExportStatusStreamOptions {
  /** The export whose status stream to open. */
  exportId: string;
  /** Fired once after the server returns 200 — resets the consumer's reconnect backoff. */
  onOpen?: () => void;
  /** Fired for each parsed `status` frame (the full ExportStatusResponse). */
  onStatus: (status: ExportStatusResponse) => void;
  /** Stream-level error (network down, 401/403, malformed). Called once before the controller aborts. */
  onError: (error: Error) => void;
  /** Called when the server closes the stream cleanly (terminal frame already delivered). */
  onComplete: () => void;
  /** Optional external abort signal. */
  signal?: AbortSignal;
}

/**
 * Opens a GET-based SSE stream for one export's status. Mirrors
 * `notifications-sse.ts`: raw `fetch()` (so it bypasses Ky's interceptors),
 * auth/tenant/locale headers read from the Zustand stores at connect time,
 * manual `\n\n` frame parsing. Every frame's `event` is `status`; `data` is the
 * full `ExportStatusResponse`. Returns an `AbortController` for teardown.
 */
export function createExportStatusStream(options: ExportStatusStreamOptions): AbortController {
  const { exportId, onOpen, onStatus, onError, onComplete, signal } = options;
  const controller = new AbortController();

  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        controller.abort();
      },
      { once: true },
    );
  }

  // Path comes from `endpoints`, never inlined here (CLAUDE.md: API URLs live in
  // one place). Hand-building `/exports/{id}/status/stream` silently 404s the
  // moment the base or the path shape changes — and being SSE, it fails quietly.
  const fullUrl = `${exportsSseBaseUrl}/${endpoints.exports.statusStream(exportId)}`;
  const { user, accessToken } = useAuthStore.getState();
  const tenantId = user?.activeTenantId;
  const locale = useLocaleStore.getState().activeLocale;

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': locale,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (tenantId) headers['X-Subsidiary-ID'] = tenantId;

  void (async () => {
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
        throw new ExportStreamError(
          response.status,
          `Export SSE stream failed: ${String(response.status)} ${response.statusText}`,
          retryAfter,
        );
      }
      if (!response.body) {
        throw new Error('Response body is null — streaming not supported');
      }

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processBlock(buffer, onStatus);
          onComplete();
          return;
        }
        buffer += decoder.decode(value, { stream: true });

        // Standard SSE framing: events are separated by a blank line (\n\n).
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          if (part.trim()) processBlock(part, onStatus);
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/**
 * Parse one SSE event block and forward its payload as an ExportStatusResponse.
 * `ping` heartbeats and malformed JSON are silently dropped; the server may also
 * emit an `error` event, surfaced so the consumer can fall back to polling.
 */
function processBlock(block: string, onStatus: (status: ExportStatusResponse) => void): void {
  let eventType = 'message';
  let dataStr = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }

  if (eventType === 'ping' || !dataStr) return;
  if (eventType !== 'status' && eventType !== 'message') return;

  try {
    const raw = JSON.parse(dataStr) as Partial<ExportStatusResponse>;
    if (raw.exportId && raw.status) onStatus(raw as ExportStatusResponse);
  } catch {
    // Malformed JSON — backend bug, not user-actionable.
  }
}
